import React from 'react';
import { Marker, Popup, Tooltip } from 'react-leaflet';
import type L from 'leaflet';
import { getManualStopReasonLabel } from '@/lib/validation/manualStopValidation';
import type { StagedManualStop } from '../types/map.types';

interface CustomerStopMarkerProps {
  stops: StagedManualStop[];
  lat: number;
  lng: number;
  icon: L.DivIcon;
  /** Opens the stop in the Stops panel, where contact details can be revealed. */
  onOpen?: (stopId: string) => void;
}

const UNVERIFIED = new Set(['unconfirmed', 'unit_missing', 'unavailable']);

const REASON_CHIP_CLASS =
  'px-2 py-1 rounded text-xs bg-muted text-muted-foreground border border-border';

const CustomerStopPopup: React.FC<{
  addressLine: string;
  unverified: boolean;
  vehicleCount: number;
  reasons: string[];
  noteFirstLine: string | undefined;
  onOpen?: () => void;
}> = ({ addressLine, unverified, vehicleCount, reasons, noteFirstLine, onOpen }) => (
  <div className='p-3 max-h-96 overflow-y-auto space-y-2'>
    <h3 className='font-bold text-lg text-foreground'>Customer stop</h3>
    <p className='text-sm text-muted-foreground'>{addressLine}</p>
    {unverified && <p className='text-xs text-muted-foreground'>Address not verified</p>}
    <div className='flex items-center gap-2 flex-wrap'>
      <span
        className='px-2 py-1 rounded text-xs font-medium'
        style={{ backgroundColor: '#0ea5e9', color: 'white' }}
      >
        {vehicleCount} {vehicleCount === 1 ? 'vehicle' : 'vehicles'}
      </span>
      {reasons.map(reason => (
        <span key={reason} className={REASON_CHIP_CLASS}>
          {reason}
        </span>
      ))}
    </div>
    {noteFirstLine && (
      <p className='text-xs text-muted-foreground truncate' title={noteFirstLine}>
        Note: {noteFirstLine}
      </p>
    )}
    {onOpen && (
      <button
        type='button'
        onClick={onOpen}
        className='text-xs font-medium text-[#0284c7] hover:underline'
      >
        View details
      </button>
    )}
  </div>
);

const CustomerStopTooltip: React.FC<{ city: string | undefined; vehicleCount: number }> = ({
  city,
  vehicleCount
}) => (
  <div className='font-bold text-sm text-center px-2 py-1'>
    Customer stop
    <span className='block text-xs font-normal text-muted-foreground'>
      {city ? `${city} · ` : ''}
      {vehicleCount} {vehicleCount === 1 ? 'vehicle' : 'vehicles'}
    </span>
  </div>
);

/**
 * Map marker for one customer household. Hover and popup never show the customer's
 * name or phone (maps get screen-shared); contact details live only in the Stops panel.
 */
export const CustomerStopMarker: React.FC<CustomerStopMarkerProps> = ({
  stops,
  lat,
  lng,
  icon,
  onOpen
}) => {
  const first = stops[0];
  if (!first) return null;

  const vehicleCount = new Set(stops.map(s => s.vin || s.id)).size;
  const reasons = Array.from(
    new Set(stops.map(s => getManualStopReasonLabel(s.manual_stop_reason)))
  );
  const street = first.address_line2
    ? `${first.dealer_address}, ${first.address_line2}`
    : first.dealer_address;
  const addressLine =
    [street, first.dealer_city, first.dealer_region].filter(Boolean).join(', ') +
    (first.dealer_postal_code ? ` ${first.dealer_postal_code}` : '');
  const noteFirstLine = first.dispatcher_note?.split('\n')[0];
  const unverified = UNVERIFIED.has(first.address_validation?.status ?? '');

  return (
    <Marker
      position={[lat, lng]}
      icon={icon}
      zIndexOffset={1100}
      eventHandlers={{ click: () => onOpen?.(first.id) }}
    >
      <Tooltip direction='top' offset={[0, -10]} opacity={0.95}>
        <CustomerStopTooltip city={first.dealer_city} vehicleCount={vehicleCount} />
      </Tooltip>
      <Popup maxWidth={350} maxHeight={400}>
        <CustomerStopPopup
          addressLine={addressLine}
          unverified={unverified}
          vehicleCount={vehicleCount}
          reasons={reasons}
          noteFirstLine={noteFirstLine}
          {...(onOpen && { onOpen: () => onOpen(first.id) })}
        />
      </Popup>
    </Marker>
  );
};
